import React, { useMemo, useRef, useState } from 'react';
import { PencilLine } from 'lucide-react';
import { makeCommandId } from '../components/InvoiceApprovalReview';
import { correctInvoice } from './api';
import { readableErrors } from './invoiceStatus';

const HEADER = [
  ['supplier_name', 'Supplier', (inv) => inv.supplier_name],
  ['invoice_no', 'Invoice number', (inv) => inv.invoice_no],
  ['date', 'Date (YYYY-MM-DD)', (inv) => inv.date],
  ['invoice_total', 'Total', (inv) => inv.totals?.invoice_total],
  ['vat_total', 'VAT', (inv) => inv.totals?.vat_total],
];
const LINE = [
  ['raw_desc', 'Description', (ln) => ln.raw_desc ?? ln.description],
  ['qty', 'Qty', (ln) => ln.qty],
  ['unit_price', 'Unit price', (ln) => ln.unit_price ?? ln.unit_cost],
  ['line_total', 'Line total', (ln) => ln.line_total],
];
export const EDITABLE_STATES = ['needs_review', 'awaiting_approval'];

/** A held invoice stores a missing figure as the text "None": show it as empty. */
const shown = (v) => (v === null || v === undefined || v === 'None' ? '' : String(v));
const key = (field, idx) => (idx === null ? field : `${field}:${idx}`);

/** What was read, as the values the form starts from. */
export function readValues(invoice) {
  const out = {};
  HEADER.forEach(([field, , get]) => { out[key(field, null)] = shown(get(invoice)); });
  (invoice.lines || []).forEach((ln, i) => LINE.forEach(([field, , get]) => { out[key(field, i)] = shown(get(ln)); }));
  return out;
}

/** Only what the person changed, in the API's shape. */
export function changedCorrections(before, after) {
  return Object.keys(after).filter((k) => (after[k] || '').trim() !== (before[k] || '').trim()).map((k) => {
    const [field, idx] = k.split(':');
    return idx === undefined ? { field, value: after[k].trim() } : { field, line_index: Number(idx), value: after[k].trim() };
  });
}

/**
 * Correct what was read. Every change is kept on the invoice as before/after (who, when); a held reading that adds up
 * after the change is matched and moved on by the server. Open by default when the reading could not be trusted.
 */
export default function CorrectReading({ invoice, startOpen = false, onChanged }) {
  const [open, setOpen] = useState(startOpen);
  const original = useMemo(() => readValues(invoice), [invoice]);
  const [values, setValues] = useState(original);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const cmd = useRef(null);

  if (!EDITABLE_STATES.includes(invoice.state)) return null;
  const changes = changedCorrections(original, values);
  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setResult(null); };

  const save = async () => {
    setBusy(true); setError(null);
    if (!cmd.current) cmd.current = makeCommandId('inv-correct');
    try {
      const out = await correctInvoice(invoice.id, { command_id: cmd.current, corrections: changes });
      cmd.current = null;
      setResult(out);
      await onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="correct-open"
        className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl border border-surface-border bg-surface text-ink hover:bg-surface-sunken">
        <PencilLine className="w-4 h-4" /> Correct what was read
      </button>
    );
  }
  const input = 'w-full min-h-[40px] rounded-lg border border-surface-border bg-surface px-2 text-ink';
  const n = changes.length;
  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-4" data-testid="correct-reading">
      <div>
        <h2 className="font-display text-lg text-ink">Correct what was read</h2>
        <p className="text-sm text-ink-muted">Change only what differs from the document. The original reading is kept beside your correction.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {HEADER.map(([field, label]) => (
          <label key={field} className="text-sm text-ink-muted">{label}
            <input className={input} value={values[key(field, null)]} onChange={set(key(field, null))} data-testid={`correct-${field}`} />
          </label>
        ))}
      </div>
      {(invoice.lines || []).length > 0 && (
        <div className="space-y-2" data-testid="correct-lines">
          {(invoice.lines || []).map((ln, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr] gap-2">
              {LINE.map(([field, label]) => (
                <label key={field} className="text-xs text-ink-muted">{i === 0 ? label : <span className="sr-only">{label}</span>}
                  <input className={input} aria-label={`${label}, line ${i + 1}`} value={values[key(field, i)]}
                    onChange={set(key(field, i))} data-testid={`correct-${field}-${i}`} />
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
      {result && (
        <p className={result.errors?.length ? 'text-warn' : 'text-primary-700'} data-testid="correct-result">
          {result.applied} change{result.applied === 1 ? '' : 's'} saved.{' '}
          {result.errors?.length ? `Still to fix: ${readableErrors(result.errors)}.` : 'The figures add up now.'}
        </p>
      )}
      {error && <p className="text-danger" data-testid="correct-error">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={busy || !n} onClick={save} data-testid="correct-save"
          className="min-h-[48px] px-5 rounded-xl bg-primary-500 text-surface font-medium disabled:opacity-50">
          {busy ? 'Saving…' : n ? `Save ${n} correction${n === 1 ? '' : 's'}` : 'Save corrections'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setValues(original); setResult(null); setError(null); }}
          data-testid="correct-cancel" className="min-h-[48px] px-5 rounded-xl border border-surface-border text-ink">Close</button>
      </div>
    </section>
  );
}
